import { useMemo, useState } from "react";
import { Download } from "lucide-react";

import OnboardingCheckbox from "../../OnboardingCheckbox";
import Panel from "../Panel";
import Skeleton from "../Skeleton";
import {
  Badge,
  BadgeTone,
  Button,
  Chip,
  Chips,
  Empty,
  PrimaryText,
  SearchInput,
  SecondaryText,
  Table,
  TableScroll,
  Td,
  Th,
  Toolbar,
  Tr,
} from "../ui";
import { PageProps, daysLabel, formatDate } from "./types";
import {
  MemberStatus,
  MemberSummary,
  daysLeft,
  memberDirectory,
} from "../../../utils/partnerMetrics";

const PAGE_SIZE = 50;

const STATUS: Record<MemberStatus, { label: string; tone: BadgeTone }> = {
  trial: { label: "Trial", tone: "amber" },
  upgraded: { label: "Upgraded", tone: "purple" },
  cancelling: { label: "Cancelling", tone: "red" },
  expired: { label: "Expired", tone: "gray" },
};

type Filter = "all" | MemberStatus;
const FILTERS: Filter[] = ["all", "trial", "upgraded", "cancelling", "expired"];

const csvCell = (v: string | number | undefined | null) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const exportCsv = (rows: MemberSummary[]) => {
  const header = ["Name", "Email", "Phone", "Status", "Plan", "First joined", "Months paid", "Current period ends", "Checked in"];
  const lines = rows.map((m) =>
    [
      `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim(),
      m.email,
      m.phone && m.phone !== "null" ? m.phone : "",
      STATUS[m.status].label,
      m.plan,
      formatDate(m.firstJoined),
      m.monthsPaid,
      formatDate(m.expiresAt),
      m.checkedIn ? "Yes" : "No",
    ]
      .map(csvCell)
      .join(",")
  );
  const blob = new Blob([[header.map(csvCell).join(","), ...lines].join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `members-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const MembersPage = ({ members, loading, capabilities, onRefresh }: PageProps) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [shown, setShown] = useState(PAGE_SIZE);

  const directory = useMemo(() => memberDirectory(members), [members]);
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: directory.length, trial: 0, upgraded: 0, cancelling: 0, expired: 0 };
    directory.forEach((m) => (c[m.status] += 1));
    return c;
  }, [directory]);

  const q = search.trim().toLowerCase();
  const visible = directory.filter(
    (m) =>
      (filter === "all" || m.status === filter) &&
      (!q ||
        `${m.firstName ?? ""} ${m.lastName ?? ""}`.toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q))
  );

  return (
    <Panel
      title="Members"
      subtitle="Everyone who has held a JackedRabbit membership at your studio"
      action={
        capabilities.canExportMembers && (
          <Button onClick={() => exportCsv(visible)} disabled={loading || visible.length === 0}>
            <Download size={15} />
            Export CSV
          </Button>
        )
      }
    >
      <Toolbar>
        <SearchInput
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShown(PAGE_SIZE);
          }}
        />
        <Chips>
          {FILTERS.map((f) => (
            <Chip
              key={f}
              $active={filter === f}
              onClick={() => {
                setFilter(f);
                setShown(PAGE_SIZE);
              }}
            >
              {f === "all" ? "All" : STATUS[f].label} · {counts[f]}
            </Chip>
          ))}
        </Chips>
      </Toolbar>

      {loading ? (
        Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} $h="36px" $mb="8px" />)
      ) : visible.length === 0 ? (
        <Empty>{q ? `No members match "${search}"` : "No members to show yet."}</Empty>
      ) : (
        <>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Member</Th>
                  <Th>Phone</Th>
                  <Th>Status</Th>
                  <Th>First joined</Th>
                  <Th $align="right">Months</Th>
                  <Th>Current period</Th>
                  <Th>Checked in</Th>
                </tr>
              </thead>
              <tbody>
                {visible.slice(0, shown).map((m) => {
                  const d = daysLeft(m.expiresAt);
                  const active = m.status !== "expired";
                  return (
                    <Tr key={m.key}>
                      <Td>
                        <PrimaryText>
                          {m.firstName} {m.lastName}
                        </PrimaryText>
                        <SecondaryText>{m.email}</SecondaryText>
                      </Td>
                      <Td data-label="Phone" $muted>{m.phone && m.phone !== "null" && m.phone !== "undefined" ? m.phone : "—"}</Td>
                      <Td data-label="Status">
                        <Badge $tone={STATUS[m.status].tone}>{STATUS[m.status].label}</Badge>
                        {active && <SecondaryText>{m.plan}</SecondaryText>}
                      </Td>
                      <Td data-label="First joined" $muted>{formatDate(m.firstJoined)}</Td>
                      <Td data-label="Months" $align="right">{m.monthsPaid}</Td>
                      <Td data-label="Current period">
                        {active ? (
                          <>
                            <PrimaryText>{daysLabel(d)} left</PrimaryText>
                            <SecondaryText>Ends {formatDate(m.expiresAt)}</SecondaryText>
                          </>
                        ) : (
                          <SecondaryText>Ended {formatDate(m.expiresAt)}</SecondaryText>
                        )}
                      </Td>
                      <Td data-label="Checked in">
                        {active ? (
                          <OnboardingCheckbox
                            membershipId={m.currentRow.membershipId}
                            isOnboarded={!!m.currentRow.isOnboarded}
                            onboardedAt={m.currentRow.onboardedAt}
                            isExpired={false}
                            onUpdate={onRefresh}
                          />
                        ) : (
                          <SecondaryText>{m.checkedIn ? "Yes" : "Never"}</SecondaryText>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
          {visible.length > shown && (
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Button onClick={() => setShown((n) => n + PAGE_SIZE)}>
                Show more ({visible.length - shown} remaining)
              </Button>
            </div>
          )}
        </>
      )}
    </Panel>
  );
};

export default MembersPage;
